import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import logo from "../images/clbg.png";
import hero from "../images/sideimage.png";
import { usePermissions } from "../context/PermissionContext";
import { useUser } from "../context/UserContext";
import LoadingComponent from "./ui/LoadingComponent";
import { getToken } from "firebase/messaging";
import { messaging } from "../firebase.js";
import { handleApiResponse, handleCustomResponse } from "../utils/response.js";

const api = axios.create({
  baseURL: "https://pevbackend.codovio.com/public/api",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

const SignInLayer = () => {
  const { isAuthenticated, isLoading, refreshAuthStatus } = usePermissions();
  const { updateUserData } = useUser();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [remember, setRemember] = useState(false);
  const [status, setStatus] = useState({
    isLoading: false,
    message: "",
    type: "",
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const emailInputRef = useRef(null);

  useEffect(() => {
    const email = localStorage.getItem("sign-in-email");
    if (email) {
      setFormData((prev) => ({
        ...prev,
        email: email,
      }));
      setRemember(true);
    } else {
      setRemember(false);
    }

    emailInputRef.current?.focus();

    if (location.state?.message) {
      if (location.state.type === "error") {
        handleCustomResponse("error", location.state.message);
      } else {
        handleCustomResponse("success", location.state.message);
      }
      window.history.replaceState({}, document.title);
    }

    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [location, isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      return handleCustomResponse("error", "All fields are required");
    }

    setStatus((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await axios.post(
        "https://pevbackend.codovio.com/public/api/login",
        {
          email: formData.email,
          password: formData.password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data.access_token) {
        throw new Error("No access token received");
      }
      const accessToken = response.data.access_token;
      let user = {
        user_id: response.data.data.user_id,
        username: response.data.data.username || "User",
        email: response.data.data.email || "",
        role_name:
          response.data.data.role_name ||
          response.data.data.role?.role_name ||
          null,
        profile_picture: response.data.data.profile_picture || null,
      };

      if (!user.role_name) {
        const userResponse = await axios.get(
          `https://pevbackend.codovio.com/public/api/get-user-by-id/${user.user_id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        user.role_name =
          userResponse.data.data?.role_name ||
          userResponse.data.data?.role?.role_name ||
          "Unknown Role";
      }

      const expiryTime = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("token_expiry", expiryTime.toString());
      localStorage.setItem("user", JSON.stringify(user));

      if (remember) {
        localStorage.setItem("sign-in-email", formData.email);
      } else {
        localStorage.removeItem("sign-in-email");
      }

      updateUserData(user);

      axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

      await refreshAuthStatus();

      handleFCMToken(accessToken, user.user_id);
      const handleRes = response.data;
      handleApiResponse(handleRes, "success", "POST");

      setStatus({ isLoading: false, message: "", type: "" });

      setTimeout(() => {
        navigate("/", { replace: true });
      }, 100);
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Error", "POST");
      let errorMessage = "Login failed";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      if (error.response?.status === 401 || error.response?.status === 422) {
        setFormData((prev) => ({ ...prev, password: "" }));
      }

      setStatus({ isLoading: false, message: "", type: "" });
    }
  };

  const handleFCMToken = async (accessToken, userId) => {
    try {
      const fcmToken = await getToken(messaging, {
        vapidKey:
          "BJyo7Gej1zE3vlOcUHb2-NtsWGercAyDo7ZsqnSbL7pS-IFIFY1a3-P0Fup2n2Y8FhfM5j7eepXGNxOPcmjIUTY",
      });

      if (fcmToken) {
        await axios.post(
          "https://pevbackend.codovio.com/public/api/save/fcm-token",
          {
            device_token: fcmToken,
            user_id: userId,
            device_type: "web",
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        console.log("✅ FCM Token sent to backend");
      } else {
        console.warn("⚠️ FCM token not available");
      }
    } catch (error) {
      console.error("❌ FCM token error:", error);
    }
  };

  if (isLoading && isAuthenticated === null) {
    return <LoadingComponent />;
  }

  return (
    <>
      <style>
        {`
          .auth-section {
            min-height: 142.857vh;
            display: flex;
            background: linear-gradient(to bottom right, #e0e7ff, #f3e8ff);
            align-items: stretch;
          }
          .auth-left {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            padding: 2rem;
            text-align: center;
          }
          .auth-left img {
            width: 22rem;
            max-width: 100%;
            margin-bottom: 1.5rem;
            transition: transform 0.3s;
          }
          .auth-left img:hover {
            transform: scale(1.05);
          }
          .auth-left h2 {
            font-size: 2.25rem;
            font-weight: bold;
            color: #1f2937;
            margin: 0 0 1rem;
          }
          .auth-left p {
            font-size: 1.125rem;
            color: #6b7280;
            max-width: 24rem;
          }
          .auth-right {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2rem;
          }
          .auth-form {
            width: 100%;
            max-width: 28rem;
            background: white;
            border-radius: 1rem;
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
            padding: 2rem;
          }
          .auth-form h3 {
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0 0 0.5rem;
            text-align: center;
          }
          .auth-form p {
            color: #6b7280;
            margin: 0 0 1.5rem;
            text-align: center;
          }
          .auth-form .form-group {
            position: relative;
            margin-bottom: 1.5rem;
          }
          .auth-form label {
            display: block;
            font-size: 0.875rem;
            color: #6b7280;
            margin-bottom: 0.25rem;
            text-align: left;
          }
          .auth-form input {
            width: 100%;
            padding: 0.75rem 1rem 0.75rem 2.5rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.5rem;
            transition: all 0.3s;
            font-size: 1rem;
            box-sizing: border-box;
          }
          .auth-form input:focus {
            outline: none;
            border-color: #dc3545;
          }
          .auth-form input.is-invalid {
            border-color: #dc3545;
          }
          .auth-form .input-icon-wrapper {
            position: relative;
          }
          .auth-form .icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: #9ca3af;
            font-size: 1.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
          }
          .auth-form .password-toggle {
            position: absolute;
            right: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: #9ca3af;
            cursor: pointer;
            font-size: 1.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 5;
          }
          .auth-form .password-toggle:hover {
            color: #E63C23;
          }
          .auth-form .password-hint {
            font-size: 0.75rem;
            color: #dc3545;
            margin-top: 0.5rem;
            text-align: left;
          }
          .auth-form .checkbox-group {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
          }
          .auth-form .checkbox-group .remember-me {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
          }
          .auth-form .checkbox-group input[type="checkbox"] {
            width: 20px;
            height: 20px;
            margin: 0;
            padding: 0;
            flex-shrink: 0;
            cursor: pointer;
            border: 2px solid #e5e7eb;
            border-radius: 4px;
            appearance: none;
            background: #fff;
            position: relative;
          }
          .auth-form .checkbox-group input[type="checkbox"]:checked {
            background: #dc3545;
            border-color: #dc3545;
          }
          .auth-form .checkbox-group input[type="checkbox"]:checked::after {
            content: '';
            position: absolute;
            left: 6px;
            top: 2px;
            width: 6px;
            height: 12px;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }
          .auth-form .checkbox-group label {
            font-size: 0.875rem;
            color: #6b7280;
            line-height: 1.25;
            cursor: pointer;
          }
          .auth-form .checkbox-group .forgot-password {
            font-size: 0.875rem;
            color: #6b7280;
          }
          .auth-form .checkbox-group .forgot-password a {
            color: #E63C23;
            text-decoration: none;
            font-weight: 500;
          }
          .auth-form .checkbox-group .forgot-password a:hover {
            text-decoration: underline;
          }
          @media (max-width: 768px) {
            .auth-form .checkbox-group {
              flex-direction: column;
              align-items: flex-start;
              gap: 0.5rem;
            }
            .auth-form .checkbox-group .forgot-password {
              align-self: flex-end;
            }
          }
          .auth-form .submit-button {
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
          }
          .auth-form button {
            width: auto;
            padding: 0.5rem 1.5rem;
            background: #E63C23;
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            font-weight: 500;
            transition: background 0.3s;
            cursor: pointer;
          }
          .auth-form button:hover {
            background: #e14f38ff;
          }
          .auth-form button:disabled {
            background: #fca5a5ff;
            cursor: not-allowed;
          }
          .auth-form .register-link {
            text-align: center;
            margin-top: 1.5rem;
            font-size: 0.875rem;
            color: #6b7280;
          }
          .auth-form .register-link a {
            color: #E63C23;
            text-decoration: none;
            font-weight: 500;
          }
          .auth-form .register-link a:hover {
            text-decoration: underline;
          }
          @media (max-width: 768px) {
            .auth-section {
              flex-direction: column;
            }
            .auth-left,
            .auth-right {
              flex: none;
              width: 100%;
            }
            .auth-left {
              padding: 2rem 1rem;
            }
            .auth-left img {
              width: 16rem;
            }
            .auth-left h2 {
              font-size: 1.75rem;
            }
            .auth-left p {
              font-size: 1rem;
            }
            .auth-right {
              padding: 1rem;
            }
            .auth-form {
              padding: 1.5rem;
            }
          }
        `}
      </style>

      <section className="auth-section">
        <div className="auth-left">
          <img src={hero} alt="Hero Image" />
          <h2>Welcome to Park Vision</h2>
          <p>Sign in to continue your journey with us!</p>
        </div>

        <div className="auth-right">
          <div className="auth-form">
            <h3>Sign In</h3>
            <p>Enter your credentials to access your account</p>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-icon-wrapper">
                  <span className="icon">
                    <Icon icon="mage:email" />
                  </span>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    ref={emailInputRef}
                    className={status.message && status.type === "error" ? "is-invalid" : ""}
                    aria-describedby="email-error"
                  />
                </div>
                {status.message && status.type === "error" && (
                  <p id="email-error" className="password-hint">
                    {status.message}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-icon-wrapper">
                  <span className="icon">
                    <Icon icon="solar:lock-password-outline" />
                  </span>
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="password"
                    id="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={status.message && status.type === "error" ? "is-invalid" : ""}
                    aria-describedby="password-error"
                  />
                  <span
                    className="password-toggle"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    title={passwordVisible ? "Hide password" : "Show password"}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && setPasswordVisible(!passwordVisible)
                    }
                  >
                    <Icon
                      icon={passwordVisible ? "ri:eye-off-line" : "ri:eye-line"}
                    />
                  </span>
                </div>
                {status.message && status.type === "error" && (
                  <p id="password-error" className="password-hint">
                    {status.message}
                  </p>
                )}
              </div>

              <div className="checkbox-group">
                <div className="remember-me">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={remember}
                    onChange={() => setRemember(!remember)}
                  />
                  <label htmlFor="remember">Remember me</label>
                </div>
                <div className="forgot-password">
                  <Link to="/forgot-password">Forgot Password?</Link>
                </div>
              </div>

              <div className="submit-button">
                <button type="submit" disabled={status.isLoading}>
                  {status.isLoading ? "Please wait..." : "Sign In"}
                </button>
              </div>

              <p className="register-link">
                Don't have an account? <Link to="/sign-up">Sign Up</Link>
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default SignInLayer;